import * as React from "react";

import { ResponsiveDialog } from "@replohq/lattice/blocks/modal/ResponsiveDialog";
import { Button } from "@replohq/lattice/components/button/Button";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";

import { AgentLaunchLetter } from "./AgentLaunchLetter";

// Founder-letter interstitial; owns all four funnel events (`viewed` / `entered` / `dismissed` / `videoPlayed`).
export const AgentLaunchModal: React.FC<{
  open: boolean;
  source: string;
  onEnterBeta: () => void;
  onDecline: (method: "not_now" | "close") => void;
}> = ({ open, source, onEnterBeta, onDecline }) => {
  const logEvent = useLogAnalytics();

  // eslint-disable-next-line replo/no-use-effect -- Analytics on a visibility transition the parent drives via props.
  React.useEffect(() => {
    if (open) {
      logEvent("agent.beta.launchModal.viewed", { source });
    }
  }, [open, logEvent, source]);

  const handleFirstVideoPlay = React.useCallback(() => {
    logEvent("agent.beta.launchModal.videoPlayed", { source });
  }, [logEvent, source]);

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          logEvent("agent.beta.launchModal.dismissed", {
            source,
            method: "close",
          });
          onDecline("close");
        }
      }}
      accessibleTitle="Meet the new Replo"
      size="base"
      UNSAFE_className="border-0 bg-neutral-300 ring-0 sm:max-w-xl"
      footer={
        <div className="flex w-full items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              logEvent("agent.beta.launchModal.dismissed", {
                source,
                method: "not_now",
              });
              onDecline("not_now");
            }}
          >
            No thanks
          </Button>
          <Button
            onClick={() => {
              logEvent("agent.beta.launchModal.entered", { source });
              onEnterBeta();
            }}
          >
            Try the beta
          </Button>
        </div>
      }
    >
      <div className="max-h-[68vh] overflow-y-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <AgentLaunchLetter onFirstVideoPlay={handleFirstVideoPlay} />
      </div>
    </ResponsiveDialog>
  );
};
